import type { Language } from "@/constants";
import { LANGUAGES } from "@/constants";
import type faTranslation from "@/i18n/translation/fa.json";
import type faZod from "@/i18n/zod/fa.json";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation as originalUseTranslation } from "react-i18next";

type DotNotationKeys<T, Prefix extends string = ""> = {
	[K in keyof T]: T[K] extends object
		? DotNotationKeys<T[K], `${Prefix}${K & string}.`>
		: `${Prefix}${K & string}`;
}[keyof T];

export type TranslationKeys =
	| DotNotationKeys<typeof faTranslation>
	| DotNotationKeys<typeof faZod>;

export type TranslationFunction = (
	key: TranslationKeys,
	options?: Parameters<TFunction>[1],
) => string;

export const useTranslation = () => {
	const { t: originalT, i18n } = originalUseTranslation();
	const lang = i18n.language as Language;

	const [isRtl, setIsRtl] = useState<boolean>(() => {
		const lang = i18n.language as Language;
		return LANGUAGES[lang]?.mode === "rtl";
	});

	useEffect(() => {
		const handleLanguageChange = (lng: string) => {
			const lang = lng as Language;
			setIsRtl(LANGUAGES[lang]?.mode === "rtl");
		};

		i18n.on("languageChanged", handleLanguageChange);

		return () => {
			i18n.off("languageChanged", handleLanguageChange);
		};
	}, [i18n]);

	function t(key: TranslationKeys, options?: Parameters<TFunction>[1]): string {
		return originalT(key, options);
	}

	return { t, isRtl, i18n, lang };
};
